/**
 * /operations/payroll-agent — Rawatib's payroll command surface
 * (Pack 3 v2 UI).
 *
 * Surfaces Rawatib's autonomous cycle execution: anomaly scan
 * results, WPS readiness, ZATCA data feed, retro adjustments,
 * negative-net cases. Read-only at this PR; sign-off action lands
 * with M3X.9 EOS+Dispute keystone integration.
 */


import { PageHeader } from "../components/ui";
import { AgentBadge } from "../components/ui/AgentBadge";

export function PayrollAgentPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Payroll Agent (Rawatib)"
        subtitle="Rawatib runs the payroll cycle autonomously. HR signs off on final disbursement."
      />

      <section className="bg-surface-default border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <AgentBadge agent="rawatib" />
          <h3 className="text-base font-semibold">
            Continuous payroll cycle
          </h3>
        </div>
        <ul className="space-y-1.5 text-sm text-text-secondary">
          <li>
            <span className="font-medium">Auto-schedule</span> · Period
            close on the locked tenant cadence (commandment 64 — runs
            via Mutaba'aa's scheduler).
          </li>
          <li>
            <span className="font-medium">Auto-compute</span> · All
            line items (basic + allowances + GOSI splits + deductions)
            per the 8-layer override stack.
          </li>
          <li>
            <span className="font-medium">Auto-generate</span> · Mudad
            WPS file + ZATCA wage data feed for Saudi-owned LEs.
          </li>
          <li>
            <span className="font-medium">Auto-handle</span> ·
            Negative-net via the Article 90 cap (with the excess
            deferred to the next period).
          </li>
          <li>
            <span className="font-medium">Auto-veto by Mutaba'aa</span> ·
            Article 90 cap on aggregate deductions; Article 95
            garnishment priority.
          </li>
          <li>
            <span className="font-medium">Escalate to HR</span> ·
            Final disbursement sign-off; any anomaly Rawatib can't
            classify.
          </li>
        </ul>
      </section>

      <section className="bg-surface-default border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <AgentBadge agent="rawatib" />
          <h3 className="text-base font-semibold">
            Retro adjustment engine
          </h3>
        </div>
        <p className="text-sm text-text-secondary">
          When a backdated salary / contract / position / grade change
          arrives, Rawatib re-bases the affected prior periods and
          posts discrete retro line items in the current period.
          Every retro adjustment writes one event +
          agent_reasoning_traces row attributing the reason.
        </p>
      </section>

      <section className="bg-surface-default border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <AgentBadge agent="mutabaa" />
          <h3 className="text-base font-semibold">
            Statutory vetos active at posting time
          </h3>
        </div>
        <ul className="text-sm text-text-secondary space-y-1.5">
          <li>
            <strong>Article 90</strong> — aggregate deductions cap of
            1/3 monthly basic wage (mode-invariant).
          </li>
          <li>
            <strong>Article 95</strong> — court-ordered garnishment
            priority over voluntary deductions.
          </li>
          <li>
            <strong>Article 107-108</strong> — overtime premium
            verification (1.5x / 2x for holidays).
          </li>
          <li>
            <strong>Mudad/WPS</strong> — &gt;85% match rule on the
            generated file.
          </li>
        </ul>
      </section>
    </div>
  );
}
